import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Search, X, Loader2, AlertCircle } from 'lucide-react'
import { getMatchFn } from '#/server/match.functions'
import { useCricketStore } from '#/store/cricketStore'

interface MatchSearchModalProps {
  isOpen: boolean
  onClose: () => void
}

export function MatchSearchModal({ isOpen, onClose }: MatchSearchModalProps) {
  const navigate = useNavigate()
  const [matchIdInput, setMatchIdInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const savedMatches = useCricketStore((state) => state.savedMatches)

  if (!isOpen) return null

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    const raw = matchIdInput.trim()
    if (!raw) return

    // Allow user to enter either just the numeric/unique part or with prefix
    const cleanId = raw.toLowerCase().startsWith('match_') ? raw : `match_${raw}`
    const rawLower = raw.toLowerCase()

    setLoading(true)
    setError(null)

    try {
      // First check local matches (matches either cleanId, raw, or id with match_ stripped)
      const local = savedMatches.find(
        (m) =>
          m.id.toLowerCase() === cleanId.toLowerCase() ||
          m.id.toLowerCase() === rawLower ||
          m.id.replace(/^match_/, '').toLowerCase() === rawLower
      )
      if (local) {
        setLoading(false)
        onClose()
        navigate({
          to: '/match/$matchId',
          params: { matchId: local.id },
          search: { mode: 'view' },
        })
        return
      }

      // Check Cloudflare D1 server with normalized ID
      let res = await getMatchFn({ data: { id: cleanId } })
      if (!res.match && cleanId !== raw) {
        res = await getMatchFn({ data: { id: raw } })
      }

      if (res.match) {
        setLoading(false)
        onClose()
        navigate({
          to: '/match/$matchId',
          params: { matchId: res.match.id },
          search: { mode: 'view' },
        })
        return
      }

      setError('Match not found. Please check the Match ID.')
    } catch {
      setError('Unable to fetch match. Please check the Match ID or network connection.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-in fade-in">
      <div className="w-full max-w-sm theme-card rounded-2xl p-5 shadow-2xl border border-[var(--theme-border)] space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-full bg-[var(--color-cricket-primary)]/10 text-[var(--color-cricket-primary)]">
              <Search className="size-5" />
            </div>
            <h3 className="text-base font-bold text-gray-900 dark:text-white">Watch Match</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10 text-gray-500"
          >
            <X className="size-5" />
          </button>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400">
          Enter any Match ID to watch the live scores and scorecard directly as a viewer.
        </p>

        <form onSubmit={handleSearch} className="space-y-4">
          <div>
            <label className="text-xs font-semibold theme-text-primary block mb-1">
              Match ID
            </label>
            <input
              type="text"
              value={matchIdInput}
              onChange={(e) => {
                setMatchIdInput(e.target.value)
                setError(null)
              }}
              placeholder="e.g. 1793549..."
              autoFocus
              className="w-full px-3 py-2.5 rounded-xl border border-[var(--theme-border)] bg-transparent text-sm text-[var(--theme-text)] focus:outline-none focus:border-[var(--color-cricket-primary)]"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 dark:bg-red-950/30 dark:text-red-400 p-2.5 rounded-lg border border-red-200 dark:border-red-900">
              <AlertCircle className="size-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !matchIdInput.trim()}
              className="theme-btn-primary px-5 py-2 rounded-lg text-xs font-semibold shadow-xs flex items-center gap-1.5 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" />
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <Search className="size-3.5" />
                  <span>View Match</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

